import { useState, useEffect } from "react";
import { Copy, Check, Calendar } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { API_URL } from "../../../src/lib/api/config";
import { useTranslation } from "../../context/TranslationContext";
import { ModalWrapper, ModalHeader, ModalBody, ModalFooter, ErrorBox, Button, LoadingSpinner, ModalTabs } from "../common";

interface SubscriptionModalProps {
	userId: string;
	site: string;
	onClose: () => void;
}

type Tab = "outlook" | "google" | "apple" | "other";

export function SubscriptionModal({
	userId,
	site,
	onClose,
}: SubscriptionModalProps) {
	const { t } = useTranslation();
	const { token } = useAuth();
	const [subscriptionUrl, setSubscriptionUrl] = useState<string>("");
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [copied, setCopied] = useState(false);
	const [activeTab, setActiveTab] = useState<Tab>("outlook");

	useEffect(() => {
		const fetchSubscriptionUrl = async () => {
			try {
				const response = await fetch(
					`${API_URL}/agenda/${site}/${userId}/subscribe`,
					{
						headers: {
							Authorization: `Bearer ${token}`,
						},
					}
				);

				if (!response.ok) {
					const failedMsg = t('subscription.failedToFetchUrl');
					throw new Error(
						Array.isArray(failedMsg)
							? failedMsg[0] || ''
							: failedMsg || ''
					);
				}

				const data = await response.json();
				setSubscriptionUrl(data.subscriptionUrl);
			} catch (err) {
				const fallbackMsg = t('errors.somethingWentWrong');
				setError(
					err instanceof Error
						? err.message
						: Array.isArray(fallbackMsg)
							? fallbackMsg[0] || ''
							: fallbackMsg || ''
				);
			} finally {
				setLoading(false);
			}
		};

		fetchSubscriptionUrl();
	}, [site, userId, token, t]);

	const handleCopy = async () => {
		try {
			await navigator.clipboard.writeText(subscriptionUrl);
			setCopied(true);
			setTimeout(() => setCopied(false), 2000);
		} catch (err) {
			console.error("Failed to copy URL:", err);
		}
	};

	const tabs = [
		{ id: 'outlook' as const, label: t('subscription.outlook') },
		{ id: 'google' as const, label: t('subscription.googleCalendar') },
		{ id: 'apple' as const, label: t('subscription.appleCalendar') },
		{ id: 'other' as const, label: t('subscription.otherApplications') },
	];

	const TabContent = ({ tab }: { tab: Tab }) => {
		const instructions = t(`subscription.${tab}Instructions`, { returnObjects: true });
		
		// Ensure instructions is an array before mapping
		const instructionItems = Array.isArray(instructions) ? instructions : [];
		
		return (
			<ol className="list-decimal list-inside space-y-0 ml-4">
				{instructionItems.map((instruction, index) => (
					<li key={index}>{instruction}</li>
				))}
			</ol>
		);
	};


	return (
		<ModalWrapper data-tsx-id="subscription-modal" onClose={onClose} size="lg">
			<ModalHeader
				icon={<Calendar />}
				title={t('subscription.calendarSubscription')}
				onClose={onClose}
			/>

			<ModalBody>
				{error ? (
					<ErrorBox
						title={t('errors.error')}
						message={error}
					/>
				) : loading ? (
					<div className="flex items-center justify-center py-8">
						<LoadingSpinner size="md" />
					</div>
				) : (
					<>
						<div className="space-y-4">
							<h3 className="text-lg font-medium text-zinc-900">
								{t('subscription.subscribeToYourCalendar')}
							</h3>
							<p className="text-zinc-600">
								{t('subscription.subscriptionInstructions')}
							</p>

							<div className="flex items-center space-x-2">
								<input
									type="text"
									value={subscriptionUrl}
									readOnly
									className="flex-1 p-2 border rounded-md bg-zinc-50 text-zinc-700 font-mono text-sm"
								/>
								<button
									onClick={handleCopy}
									className="p-2 text-blue-600 hover:text-blue-700 rounded-md border border-blue-200 hover:bg-blue-50"
									title={t('subscription.copyUrl')}
								>
									{copied ? (
										<Check className="w-5 h-5" />
									) : (
										<Copy className="w-5 h-5" />
									)}
								</button>
							</div>
						</div>

						<ModalTabs
							tabs={tabs}
							activeTab={activeTab}
							onTabChange={(tabId) => setActiveTab(tabId as Tab)}
						>
							<div className="min-h-[220px] p-2">
								<h4 className="font-medium text-zinc-900 mb-4">
									{t('subscription.instructionsFor', {
										app: activeTab === "outlook"
											? t('subscription.outlook')
											: activeTab === "google"
												? t('subscription.googleCalendar')
												: activeTab === "apple"
													? t('subscription.appleCalendar')
													: t('subscription.otherApplications')
									})}
								</h4>
								<div className="text-zinc-600">
									<TabContent tab={activeTab} />
								</div>
							</div>
						</ModalTabs>
					</>
				)}
			</ModalBody>

			<ModalFooter alignment="right">
				<Button
					variant="secondary"
					onClick={onClose}
				>
					{t('common.close')}
				</Button>
			</ModalFooter>
		</ModalWrapper>
	);
}